// Registrazioni dall'app ("Diventa cliente"): chi è entrato senza essere in anagrafica ha mandato i suoi dati.
// Lo studio controlla e crea il cliente (o collega il contatto a un cliente già presente, stesso codice fiscale).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, UserPlus, Link2, Ban, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio/registrazioni`;
const dataIt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const dataOra = (s) => (s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');
const aggiornaBadge = () => window.dispatchEvent(new Event('jvservice:registrazioni-app'));
const STATI = [['', 'Cronologia (tutte)'], ['in_attesa', 'Da gestire'], ['creata', 'Clienti creati'], ['collegata', 'Collegati'], ['scartata', 'Scartate'], ['ritirata', 'Ritirate']];

function Riga({ r, onFatto }) {
  const [motivo, setMotivo] = useState('');
  const [invio, setInvio] = useState(false);
  const d = r.dati;
  const azione = async (percorso, corpo, messaggio) => {
    setInvio(true);
    try { await axios.post(`${API}/${r.id}/${percorso}`, corpo); toast.success(messaggio); onFatto(); aggiornaBadge(); } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setInvio(false);
  };
  return (
    <Card><CardContent className="p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-lg">{d.last_name} {d.first_name}</span>
        <Badge className="bg-slate-100 text-slate-700">{r.contatto}</Badge>
        <span className="text-sm text-slate-500 ml-auto">{dataOra(r.quando)}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <div>Sesso: <b>{d.gender}</b> · Nato il <b>{dataIt(d.birth_date)}</b> a <b>{d.birth_abroad ? d.birth_country : `${d.birth_comune} (${d.birth_provincia})`}</b></div>
        <div>Codice fiscale: <b className="font-mono">{d.fiscal_code}</b>
          {d.cf_diverso && <span className="ml-2 text-amber-700"><AlertTriangle className="w-4 h-4 inline" /> scritto a mano, diverso da quello calcolato: verificare</span>}</div>
        <div>Residenza: <b>{d.address} {d.address_number}, {d.postal_code} {d.city} ({d.city_provincia})</b></div>
      </div>
      {r.stato === 'in_attesa' && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {r.doppione ? (
            <>
              <span className="text-sm text-amber-800">Già in anagrafica: <b>{r.doppione.last_name} {r.doppione.first_name}</b> ({r.doppione.phone || r.doppione.email || 'senza contatti'})</span>
              <Button size="sm" disabled={invio} onClick={() => azione('collega', { client_id: r.doppione.id }, 'Contatto collegato al cliente')}><Link2 className="w-4 h-4 mr-1" />Collega al cliente esistente</Button>
            </>
          ) : (
            <Button size="sm" disabled={invio} onClick={() => azione('crea', undefined, 'Cliente creato in anagrafica')}><UserPlus className="w-4 h-4 mr-1" />Crea cliente</Button>
          )}
          <Input className="max-w-xs h-8" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (lo vede la persona)" />
          <Button size="sm" variant="outline" disabled={invio || !motivo.trim()} onClick={() => azione('scarta', { motivo }, 'Registrazione scartata')}><Ban className="w-4 h-4 mr-1" />Scarta</Button>
        </div>
      )}
      {r.stato !== 'in_attesa' && (
        <div className="text-xs text-slate-500">
          {{ creata: 'Cliente creato', collegata: 'Collegata a cliente esistente', scartata: 'Scartata', ritirata: 'Ritirata' }[r.stato] || r.stato} da {r.gestita_da} il {dataOra(r.gestita_il)}{r.motivo ? ` · motivo: ${r.motivo}` : ''}
        </div>
      )}
    </CardContent></Card>
  );
}

export default function RegistrazioniApp() {
  const [stato, setStato] = useState('');   // si apre sulla cronologia (tutte)
  const [elenco, setElenco] = useState(null);
  const carica = useCallback(() => {
    setElenco(null);
    axios.get(API, { params: { stato } }).then((r) => setElenco(r.data.registrazioni)).catch(() => setElenco([]));
  }, [stato]);
  useEffect(() => { carica(); }, [carica]);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Registrazioni app</h1>
      <p className="text-sm text-slate-500">Persone entrate nell'app senza essere in anagrafica che hanno mandato i loro dati («Diventa cliente»).
        Creando il cliente (o collegando il contatto) al prossimo accesso vedranno le loro pratiche.</p>
      <div className="flex flex-wrap gap-2">{STATI.map(([k, t]) => <Button key={k || 'tutte'} variant={stato === k ? 'default' : 'outline'} onClick={() => setStato(k)}>{t}</Button>)}</div>
      {elenco === null ? <Loader2 className="w-5 h-5 animate-spin" /> : !elenco.length ? <p className="text-sm">Nessuna registrazione.</p>
        : elenco.map((r) => <Riga key={r.id} r={r} onFatto={carica} />)}
    </div>
  );
}
